import axios from 'axios';
import type { AxiosError } from 'axios';
import { buildSaasHeaders, resolveSaasContext } from '@/uptk/core/api/saasContext';
import type {
  TenantThemeConfiguration,
  TenantThemeCatalogResponse,
  TenantThemeResponse,
  TenantThemeSource,
  TenantThemeUpdateRequest,
  ThemeCatalogItem,
  ThemeAssetConfiguration,
  ThemeMode,
  ThemeModeConfiguration,
  ThemeShapeConfiguration,
  ThemeScope,
  ThemeScopeConfiguration,
  ThemeTokens,
  ThemeTypographyConfiguration,
  UserThemePreferenceResponse,
  UserThemePreferenceUpdateRequest,
} from '@/types/theme_configuration';
import { is_theme_mode, is_theme_scope } from '@/types/theme_configuration';

type ApiOptions = {
  tenantId?: string;
};

type ErrorDetails = {
  timestamp?: string;
  message?: string;
  details?: string[];
  error?: string;
};

export class TenantThemeApiError extends Error {
  status: number | null;
  details: string[];
  code: string | null;

  constructor(message: string, status: number | null, details: string[] = [], code: string | null = null) {
    super(message);
    this.name = 'TenantThemeApiError';
    this.status = status;
    this.details = details;
    this.code = code;
  }
}

const is_record = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const build_headers = (options: ApiOptions = {}) => {
  const context = resolveSaasContext({ tenantId: options.tenantId });
  return buildSaasHeaders(context);
};

const normalize_tokens = (value: unknown): ThemeTokens => {
  if (!is_record(value)) {
    return {};
  }

  return Object.entries(value).reduce<ThemeTokens>((accumulator, [key, entry]) => {
    if (typeof entry === 'string' && entry.trim().length > 0) {
      accumulator[key as keyof ThemeTokens] = entry.trim();
    }
    return accumulator;
  }, {});
};

const normalize_assets = (value: unknown): ThemeAssetConfiguration => {
  if (!is_record(value)) {
    return {};
  }

  return {
    logoLightUrl:
      typeof value.logoLightUrl === 'string' ? value.logoLightUrl.trim() || null : null,
    logoDarkUrl:
      typeof value.logoDarkUrl === 'string' ? value.logoDarkUrl.trim() || null : null,
    faviconUrl:
      typeof value.faviconUrl === 'string' ? value.faviconUrl.trim() || null : null,
  };
};

const normalize_typography = (value: unknown): ThemeTypographyConfiguration => {
  if (!is_record(value)) {
    return {};
  }

  return {
    displayFontFamily:
      typeof value.displayFontFamily === 'string' ? value.displayFontFamily.trim() || null : null,
    bodyFontFamily:
      typeof value.bodyFontFamily === 'string' ? value.bodyFontFamily.trim() || null : null,
    labelFontFamily:
      typeof value.labelFontFamily === 'string' ? value.labelFontFamily.trim() || null : null,
  };
};

const normalize_shape = (value: unknown): ThemeShapeConfiguration => {
  if (!is_record(value)) {
    return {};
  }

  const rawCornerRadius =
    typeof value.cornerRadius === 'number'
      ? value.cornerRadius
      : typeof value.cornerRadius === 'string'
        ? Number(value.cornerRadius)
        : null;

  return {
    cornerRadius:
      typeof rawCornerRadius === 'number' && Number.isFinite(rawCornerRadius)
        ? rawCornerRadius
        : null,
  };
};

const normalize_mode_configuration = (value: unknown): ThemeModeConfiguration => {
  if (!is_record(value)) {
    return {};
  }

  return {
    tokens: normalize_tokens(value.tokens),
    assets: normalize_assets(value.assets),
    typography: normalize_typography(value.typography),
    shape: normalize_shape(value.shape),
  };
};

const normalize_scope_configuration = (value: unknown): ThemeScopeConfiguration => {
  if (!is_record(value) || !is_record(value.modes)) {
    return {};
  }

  const modes = Object.entries(value.modes).reduce<Partial<Record<ThemeMode, ThemeModeConfiguration>>>(
    (accumulator, [modeKey, modeValue]) => {
      if (is_theme_mode(modeKey)) {
        accumulator[modeKey] = normalize_mode_configuration(modeValue);
      }
      return accumulator;
    },
    {}
  );

  return { modes };
};

const normalize_configuration = (value: unknown): TenantThemeConfiguration => {
  if (!is_record(value) || !is_record(value.scopes)) {
    return { scopes: {} };
  }

  const scopes = Object.entries(value.scopes).reduce<Partial<Record<ThemeScope, ThemeScopeConfiguration>>>(
    (accumulator, [scopeKey, scopeValue]) => {
      if (is_theme_scope(scopeKey)) {
        accumulator[scopeKey] = normalize_scope_configuration(scopeValue);
      }
      return accumulator;
    },
    {}
  );

  return { scopes };
};

const normalize_theme_source = (value: unknown): TenantThemeSource | null =>
  value === 'PLATFORM_DEFAULT' || value === 'TENANT_OVERRIDE'
    ? (value as TenantThemeSource)
    : null;

const normalize_theme_catalog_item = (value: unknown): ThemeCatalogItem | null => {
  if (!is_record(value)) {
    return null;
  }

  const themeKey = typeof value.themeKey === 'string' ? value.themeKey.trim() : '';
  if (themeKey.length === 0) {
    return null;
  }

  const themeName = typeof value.themeName === 'string' && value.themeName.trim().length > 0
    ? value.themeName.trim()
    : themeKey;

  return {
    themeKey,
    themeName,
    selected: value.selected === true,
    tenantDefault: value.tenantDefault === true,
    source: normalize_theme_source(value.source),
  };
};

const normalize_theme_catalog_items = (value: unknown): ThemeCatalogItem[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.reduce<ThemeCatalogItem[]>((accumulator, entry) => {
    const normalized = normalize_theme_catalog_item(entry);
    if (normalized) {
      accumulator.push(normalized);
    }
    return accumulator;
  }, []);
};

const normalize_optional_string = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

const normalize_theme_response = (payload: unknown, tenantId = ''): TenantThemeResponse => {
  if (!is_record(payload)) {
    return {
      tenantId,
      version: null,
      source: null,
      selectedThemeKey: null,
      selectedThemeName: null,
      tenantDefaultThemeKey: null,
      availableThemes: [],
      configuration: { scopes: {} },
    };
  }

  return {
    tenantId: typeof payload.tenantId === 'string' ? payload.tenantId : tenantId,
    version: typeof payload.version === 'number' ? payload.version : null,
    updatedAt: typeof payload.updatedAt === 'string' ? payload.updatedAt : null,
    updatedBy: typeof payload.updatedBy === 'string' ? payload.updatedBy : null,
    source: normalize_theme_source(payload.source),
    selectedThemeKey: normalize_optional_string(payload.selectedThemeKey),
    selectedThemeName: normalize_optional_string(payload.selectedThemeName),
    tenantDefaultThemeKey: normalize_optional_string(payload.tenantDefaultThemeKey),
    availableThemes: normalize_theme_catalog_items(payload.availableThemes),
    configuration: normalize_configuration(payload.configuration),
  };
};

const normalize_theme_catalog_response = (
  payload: unknown,
  tenantId = ''
): TenantThemeCatalogResponse => {
  if (!is_record(payload)) {
    return {
      tenantId,
      version: null,
      selectedThemeKey: null,
      selectedThemeName: null,
      tenantDefaultThemeKey: null,
      themes: [],
    };
  }

  return {
    tenantId: typeof payload.tenantId === 'string' ? payload.tenantId : tenantId,
    version: typeof payload.version === 'number' ? payload.version : null,
    updatedAt: typeof payload.updatedAt === 'string' ? payload.updatedAt : null,
    updatedBy: typeof payload.updatedBy === 'string' ? payload.updatedBy : null,
    selectedThemeKey: normalize_optional_string(payload.selectedThemeKey),
    selectedThemeName: normalize_optional_string(payload.selectedThemeName),
    tenantDefaultThemeKey: normalize_optional_string(payload.tenantDefaultThemeKey),
    themes: normalize_theme_catalog_items(payload.themes),
  };
};

const normalize_user_theme_preference_response = (
  payload: unknown,
  tenantId = ''
): UserThemePreferenceResponse => {
  if (!is_record(payload)) {
    return {
      tenantId,
      themeKey: null,
      themeName: null,
      tenantDefaultThemeKey: null,
      availableThemes: [],
    };
  }

  return {
    tenantId: typeof payload.tenantId === 'string' ? payload.tenantId : tenantId,
    themeKey: normalize_optional_string(payload.themeKey),
    themeName: normalize_optional_string(payload.themeName),
    tenantDefaultThemeKey: normalize_optional_string(payload.tenantDefaultThemeKey),
    availableThemes: normalize_theme_catalog_items(payload.availableThemes),
  };
};

const to_tenant_theme_api_error = (error: unknown, fallbackMessage: string) => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ErrorDetails>;
    const status = axiosError.response?.status ?? null;
    const payload = axiosError.response?.data;
    const details = Array.isArray(payload?.details)
      ? payload.details.filter((entry): entry is string => typeof entry === 'string')
      : [];
    const message = payload?.message ?? axiosError.message ?? fallbackMessage;
    const code = payload?.error ?? null;
    return new TenantThemeApiError(message, status, details, code);
  }

  if (error instanceof Error) {
    return new TenantThemeApiError(error.message, null);
  }

  return new TenantThemeApiError(fallbackMessage, null);
};

export async function fetchCurrentTenantTheme(
  options: ApiOptions = {}
): Promise<TenantThemeResponse | null> {
  try {
    const response = await axios.get<unknown>('/api/v1/tenants/current/theme', {
      headers: build_headers(options),
    });
    return normalize_theme_response(response.data, options.tenantId ?? '');
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw to_tenant_theme_api_error(error, 'Failed to load tenant theme configuration.');
  }
}

export async function updateCurrentTenantTheme(
  payload: TenantThemeUpdateRequest,
  options: ApiOptions = {}
): Promise<TenantThemeResponse> {
  try {
    const response = await axios.put<unknown>('/api/v1/tenants/current/theme', payload, {
      headers: build_headers(options),
    });
    return normalize_theme_response(response.data, options.tenantId ?? '');
  } catch (error) {
    throw to_tenant_theme_api_error(error, 'Failed to update tenant theme configuration.');
  }
}

export async function deleteCurrentTenantTheme(
  options: ApiOptions & { themeKey?: string } = {}
): Promise<void> {
  try {
    await axios.delete('/api/v1/tenants/current/theme', {
      headers: build_headers(options),
      params: options.themeKey ? { themeKey: options.themeKey } : undefined,
    });
  } catch (error) {
    throw to_tenant_theme_api_error(error, 'Failed to reset tenant theme configuration.');
  }
}

export async function fetchCurrentTenantThemeCatalog(
  options: ApiOptions = {}
): Promise<TenantThemeCatalogResponse | null> {
  try {
    const response = await axios.get<unknown>('/api/v1/tenants/current/themes', {
      headers: build_headers(options),
    });
    return normalize_theme_catalog_response(response.data, options.tenantId ?? '');
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw to_tenant_theme_api_error(error, 'Failed to load tenant theme catalog.');
  }
}

export async function fetchUserThemePreference(
  options: ApiOptions = {}
): Promise<UserThemePreferenceResponse | null> {
  try {
    const response = await axios.get<unknown>('/api/v1/users/me/preferences/theme', {
      headers: build_headers(options),
    });
    return normalize_user_theme_preference_response(response.data, options.tenantId ?? '');
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw to_tenant_theme_api_error(error, 'Failed to load user theme preference.');
  }
}

export async function updateUserThemePreference(
  payload: UserThemePreferenceUpdateRequest,
  options: ApiOptions = {}
): Promise<UserThemePreferenceResponse> {
  try {
    const response = await axios.put<unknown>('/api/v1/users/me/preferences/theme', payload, {
      headers: build_headers(options),
    });
    return normalize_user_theme_preference_response(response.data, options.tenantId ?? '');
  } catch (error) {
    throw to_tenant_theme_api_error(error, 'Failed to update user theme preference.');
  }
}
